import { cssColorValue, IconArrowTopRight, Text, } from '@spotify-internal/encore-web/core';
import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { useS4AMastheadContext } from '../../S4AMastheadProvider';
import { icons } from '../../../utils/Icons';
import { ONE_COLUMN_MAX_WIDTH } from '../../NavBar/FlyoutMenu/MenuLayouts/Grid';
import { EASE, TIMEOUT_IN, TIMEOUT_OUT } from '../../NavBar/FlyoutMenu/config';
const ON_FOCUS_EASE = 'cubic-bezier(0, 0, 0, 1)';
const ON_FOCUS_TIME = '.45s';
const OFF_FOCUS_EASE = 'cubic-bezier(0, 0, 1, 0)';
const OFF_FOCUS_TIME = '.2s';
const visibleIcon = css `
  opacity: 100%;
  [dir='ltr'] & {
    padding-inline-start: ${({ size }) => {
    if (size === 'large') {
        return '8px';
    }
    if (size === 'medium') {
        return '6px';
    }
    return '4px';
}};
  }
  [dir='rtl'] & {
    padding-inline-end: ${({ size }) => {
    if (size === 'large') {
        return '8px';
    }
    if (size === 'medium') {
        return '6px';
    }
    return '4px';
}};
    padding-inline-start: 0px;
  }
`;
const withUnderline = css `
  padding-bottom: ${({ size }) => {
    if (size === 'large') {
        return '4px';
    }
    if (size === 'medium') {
        return '3px';
    }
    return '2px';
}};

  background-image: ${({ color }) => color
    ? `linear-gradient(${color}, ${color})`
    : `linear-gradient(${cssColorValue('textBase')}, ${cssColorValue('textBase')})`};

  background-repeat: no-repeat;
  background-size: ${({ size }) => size === 'large' || size === 'xlarge' ? '0% 4px' : '0% 2px'};
  background-position-y: 100%;
  background-position-x: 0%;
  [dir='rtl'] & {
    background-position-x: 100%;
  }
  transition: background-size ${OFF_FOCUS_EASE} ${OFF_FOCUS_TIME},
    background-position ${OFF_FOCUS_EASE} ${OFF_FOCUS_TIME},
    // color change transitions with flyout config
    color cubic-bezier(${EASE.join(', ')}) ${TIMEOUT_OUT}ms;
`;
const inFocus = css `
  outline: none;
  // triggers the underline animation
  background-size: ${({ size }) => size === 'large' || size === 'xlarge' ? '100% 4px' : '100% 2px'};
  transition: background-size ${ON_FOCUS_EASE} ${ON_FOCUS_TIME},
    background-position ${ON_FOCUS_EASE} ${ON_FOCUS_TIME},
    // color change transitions with flyout config
    color cubic-bezier(${EASE.join(', ')}) ${TIMEOUT_IN}ms;

  .newTabIcon {
    // animates the new tab arrow from left to right
    // and adds padding between it and the text
    transition: all ${ON_FOCUS_EASE} ${ON_FOCUS_TIME};
    ${visibleIcon}
  }
`;
const TextLinkWrapper = styled.a.withConfig({
    shouldForwardProp: prop => !['showIcon', 'fullWidth', 'isFocus', 'color', 'size'].includes(prop),
}) `
  // override default link/button styling
  text-decoration: none;
  border: none;
  color: ${({ color }) => color || cssColorValue('textBase')};
  outline: none;
  display: inline-block;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'fit-content')};
  background-color: unset;
  padding: 0;
  cursor: pointer;

  ${withUnderline}

  &:focus-visible,
  &:hover {
    @media (hover: hover) {
      ${inFocus}
    }
  }

  ${({ isFocus }) => {
    if (isFocus) {
        return inFocus;
    }
    return '';
}}
`;
const Content = styled.div.withConfig({
    shouldForwardProp: prop => !['fullWidth'].includes(prop),
}) `
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${({ fullWidth }) => fullWidth ? 'space-between' : 'unset'};
`;
const StyledText = styled(Text).withConfig({
    shouldForwardProp: prop => !['size', 'hasIcon'].includes(prop),
}) `
  line-height: normal;
  text-align: start;
  ${({ hasIcon, size }) => {
    if (hasIcon) {
        return css `
        padding-inline-end: ${() => {
            if (size === 'large' || size === 'xlarge') {
                return '8px';
            }
            if (size === 'medium') {
                return '6px';
            }
            return '4px';
        }};
      `;
    }
    return '';
}}

  // override the encore font scaling to keep 40px
  // for large links on mobile
  @media (max-width: ${ONE_COLUMN_MAX_WIDTH}) {
    ${({ size }) => size === 'large'
    ? css `
            font-size: 40px;
          `
    : ''}
  }
`;
const StyledIconArrowTopRight = styled(IconArrowTopRight) `
  display: flex;
  opacity: 0%;
  padding-inline-start: 0px;
  padding-inline-end: 0px;
  transition: padding-inline-start cubic-bezier(0, 0, 1, 0) 0.2s,
    padding-inline-end cubic-bezier(0, 0, 1, 0) 0.2s,
    opacity cubic-bezier(0, 0, 1, 0) 0.2s;

  @media (hover: none) {
    ${visibleIcon}
  }
`;
const getTextVariant = (size) => {
    switch (size) {
        case 'xsmall':
            return 'bodySmallBold';
        case 'small':
            return 'bodyMediumBold';
        case 'medium':
            return 'titleSmall';
        case 'large':
            return 'titleLarge';
        case 'xlarge':
            return 'headlineMedium';
        default:
            return 'bodyMediumBold';
    }
};
export const TextLink = ({ label, href, trackingCategory, trackingLabel, color, size, openInNewTab = false, svgIcon, onClick, onHover, onKeyDown, onMouseDown, fullWidth = false, className, isFocus, }) => {
    const Icon = svgIcon ? icons[svgIcon] : null;
    const { getLinkProps } = useS4AMastheadContext();
    const props = href
        ? getLinkProps({
            trackingAttributes: [trackingCategory, trackingLabel, 'click'],
            href: href,
            openInNewTab: openInNewTab,
        })
        : null;
    const anchorRef = useRef(null);
    return (React.createElement(TextLinkWrapper, Object.assign({ ref: anchorRef, color: color, isFocus: isFocus, className: className, size: size, fullWidth: fullWidth, showIcon: openInNewTab, tabIndex: 0 }, props, { onMouseEnter: onHover, onMouseOver: onHover, onFocus: onKeyDown, onKeyDown: onKeyDown, onMouseDown: onMouseDown, onClick: (e) => {
            var _a, _b;
            (_a = props === null || props === void 0 ? void 0 : props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
            onClick === null || onClick === void 0 ? void 0 : onClick(e);
            (_b = anchorRef.current) === null || _b === void 0 ? void 0 : _b.blur();
        }, as: href ? 'a' : 'button' }),
        React.createElement(Content, { fullWidth: fullWidth },
            React.createElement(StyledText, { hasIcon: svgIcon !== undefined || openInNewTab, size: size, variant: getTextVariant(size) }, label),
            Icon && React.createElement(Icon, { size: size === 'medium' ? 'small' : 'medium' }),
            openInNewTab && (React.createElement(StyledIconArrowTopRight, { className: "newTabIcon", size: size === 'medium' || size === 'small' ? 'small' : 'medium' })))));
};
//# sourceMappingURL=index.js.map